import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import React, { useId } from 'react';

import { TextFieldError, TextFieldTitle } from '../text-field/TextFieldTitle';
import { colors } from '../theme/theme';

import { CustomSelectWrapper } from './CommonSelectStyles';

/** Visually hides element but keeps it available for screen readers */
const srOnlyLabelSx = {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap' as const,
    border: 0,
};

interface CustomSelectProps {
    value: string | Record<string, unknown> | null | undefined;
    onChange: (value: string) => void;
    options: string[] | Array<{ id: string | number; name: string }>;
    placeholder?: string;
    /** Shown when options are empty (in placeholder and as disabled item in dropdown) */
    noOptionsMessage?: string;
    selectMaxWidth?: string | number;
    selectMinWidth?: string | number;
    maxHeight?: string | number;
    iconColor?: string;
    title?: string;
    required?: boolean;
    errorValue?: string;
    /** When true, FormControl shows error state (red border). */
    error?: boolean;
    helperTitle?: string;
    helperTitleOnClick?: () => void;
    disabled?: boolean;
    sx?: SxProps;
    maxWidth?: string | number;
    /**
     * Accessible label - creates a proper label association for ADA compliance.
     * Use labelSrOnly in tables where column header provides visual context.
     */
    ariaLabel?: string;
    /** Unique id for Select (required when multiple instances, e.g. tables). */
    id?: string;
    /** When true, label is visually hidden (screen-reader only). */
    labelSrOnly?: boolean;
    renderContractItemStyle?: boolean;
}

/** Renders contract-style content: + primary (blue bold) + bullet + secondary (grey, truncated) */
const renderContractDisplay = (name: string) => {
    const parts = typeof name === 'string' ? name.split(' · ').map(s => s.trim()) : [String(name), ''];
    const primary = parts[0] || '';
    const secondary = parts[1] || '';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
                component="span"
                variant="body2"
                sx={{
                    color: colors.bluePreset[10],
                    fontWeight: 600,
                    flexShrink: 0,
                }}
            >
                {primary}
            </Typography>
            {secondary && (
                <>
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{
                            color: colors.greyPreset[150],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        }}
                    >
                        {secondary}
                    </Typography>
                </>
            )}
        </Box>
    );
};

const SelectIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
    <span
        {...props}
        className={['icon-down-caret', props.className].filter(Boolean).join(' ')}
        style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, ...props.style, right: '12px' }}
    />
);

// Stable component reference for disabled state — inline `() => null` creates a new function
// type on every render, causing the icon to unmount/remount and flicker.
const NoIcon = () => null;

export const CustomSelect: React.FC<CustomSelectProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select',
    noOptionsMessage,
    maxHeight = 130,
    title,
    required = false,
    errorValue = '',
    error = false,
    helperTitle,
    helperTitleOnClick,
    disabled = false,
    sx,
    ariaLabel,
    id: idProp,
    labelSrOnly = false,
    renderContractItemStyle = false,
    maxWidth,
}) => {
    const theme = useTheme();
    const generatedId = useId();
    const id = idProp ?? generatedId.replace(/:/g, '-');
    const labelId = `${id}-label`;
    const inputId = `${id}-input`;
    const handleChange = (e: SelectChangeEvent) => {
        onChange(e.target.value as string);
    };

    // Check if options are objects with id and name
    const isObjectOptions =
        options.length > 0 && typeof options[0] === 'object' && 'id' in options[0] && 'name' in options[0];

    // Get display value - if selected matches an option's id, show the option's name, otherwise show selected value
    const getDisplayValue = () => {
        // If value was accidentally stored as option object (e.g. when id was ''), use its name
        if (typeof value === 'object' && value !== null && 'name' in value) {
            return (value as { name?: string }).name ?? '';
        }
        if (isObjectOptions) {
            const selectedOption = (options as Array<{ id: string; name: string }>).find(
                opt => String(opt.id) === String(value ?? ''),
            );
            // When value is '' (e.g. "All contracts"), show that option's name instead of placeholder
            return selectedOption ? selectedOption.name : typeof value === 'string' ? value : '';
        }
        if (value == null || value === '') return '';
        return typeof value === 'string' ? value : String(value);
    };
    return (
        <CustomSelectWrapper>
            {title && <TextFieldTitle title={title} required={required} />}
            <FormControl error={error}>
                {ariaLabel && (
                    <InputLabel id={labelId} htmlFor={inputId} sx={labelSrOnly ? srOnlyLabelSx : undefined}>
                        {ariaLabel}
                    </InputLabel>
                )}
                <Select
                    id={id}
                    labelId={ariaLabel ? labelId : undefined}
                    inputProps={{ id: inputId, 'aria-hidden': true, tabIndex: -1 }}
                    value={value == null || typeof value === 'object' ? '' : value}
                    onChange={handleChange}
                    disabled={disabled}
                    displayEmpty
                    IconComponent={disabled ? NoIcon : SelectIcon}
                    renderValue={() => {
                        const displayValue = getDisplayValue();
                        const emptyPlaceholder =
                            options.length === 0 && noOptionsMessage ? noOptionsMessage : placeholder;
                        if (displayValue && renderContractItemStyle) {
                            return renderContractDisplay(displayValue);
                        }
                        return displayValue ? (
                            <Typography variant="body2" color="textPrimary" noWrap sx={{ lineHeight: '21px' }}>
                                {displayValue}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="textSecondary" noWrap sx={{ lineHeight: '21px' }}>
                                {emptyPlaceholder}
                            </Typography>
                        );
                    }}
                    sx={[
                        { '& .MuiSelect-nativeInput': { visibility: 'hidden' } },
                        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                    ]}
                    MenuProps={{
                        sx: {
                            zIndex: 2000,
                        },
                        PaperProps: {
                            role: 'region',
                            ...(ariaLabel ? { 'aria-labelledby': labelId } : { 'aria-label': placeholder }),
                            sx: {
                                zIndex: 2001,
                                bgcolor: colors.fullWhite,
                                mt: 1,
                                borderRadius: theme.spacing(1),
                                boxShadow: theme.shadows[2],
                                border: `1px solid ${colors.greyPreset[30]}`,
                                overflow: 'hidden',
                                maxWidth: maxWidth,
                                '& .MuiList-root': {
                                    gap: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: maxHeight,
                                    overflow: 'auto',
                                },
                            },
                        },
                    }}
                >
                    {options.length === 0 && noOptionsMessage ? (
                        <MenuItem
                            disabled
                            sx={{ ...theme.typography.body2, color: colors.greyPreset[60], py: 1, px: 2.5 }}
                        >
                            {noOptionsMessage}
                        </MenuItem>
                    ) : (
                        options.map((item: string | { id: string | number; name: string }) => {
                            const itemId = typeof item === 'object' ? item.id : item;
                            const itemName = typeof item === 'object' ? item.name : item;
                            return (
                                <MenuItem
                                    key={isObjectOptions ? String(itemId ?? '') : itemId}
                                    value={isObjectOptions ? (itemId ?? '') : itemId}
                                    sx={{
                                        ...theme.typography.body2,
                                        color: colors.fullBlack,
                                        py: 1,
                                        px: 2.5,

                                        '&:hover': {
                                            backgroundColor: colors.primaryLight[5],
                                        },

                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.primaryLight[5]} !important`,
                                        },
                                    }}
                                >
                                    {renderContractItemStyle && itemName ? renderContractDisplay(itemName) : itemName}
                                </MenuItem>
                            );
                        })
                    )}
                </Select>
            </FormControl>
            {errorValue && <TextFieldError errorValue={errorValue} />}
            {helperTitle && (
                <Box onClick={helperTitleOnClick} sx={{ cursor: 'pointer' }}>
                    <Typography variant="subtitle2" className="helperTitle">
                        {helperTitle}
                    </Typography>
                </Box>
            )}
        </CustomSelectWrapper>
    );
};
